import { useQuery } from '@tanstack/react-query';
import { useCurrentPath, navigate } from '@/app/navigation';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Conversation, Message } from '@octery/shared';

interface ConversationWithMessages extends Conversation {
  messages: Message[];
}

export function ConversationThreadPage() {
  const path = useCurrentPath();
  const id = path.split('/conversations/')[1];

  const { data, isLoading, error } = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => api.get<ConversationWithMessages>(`/api/conversations/${id}`),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-8 w-24 bg-muted rounded animate-pulse" />
          <div className="h-6 w-48 bg-muted rounded animate-pulse" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`flex ${i % 2 ? 'justify-start' : 'justify-end'}`}>
              <div className="h-16 w-2/3 bg-muted rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/conversations')}>
          ← Back to Conversations
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Conversation not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    active: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    handed_off: 'bg-purple-100 text-purple-800',
    abandoned: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/conversations')}>
            ← Back
          </Button>
          <div>
            <h1 className="text-xl font-bold">Contact: {data.contactId}</h1>
            <p className="text-sm text-muted-foreground">
              {data.channel} · {data.messageCount} messages · Started{' '}
              {new Date(data.startedAt).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[data.status] || 'bg-gray-100'}`}
          >
            {data.status}
          </span>
          {data.lastIntent && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              {data.lastIntent}
            </span>
          )}
        </div>
      </div>

      {/* Metadata cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Status</p>
            <p className="text-sm font-medium capitalize">{data.status.replace('_', ' ')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Consult Offered</p>
            <p className="text-sm font-medium">{data.consultOffered ? 'Yes ✅' : 'No'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Manager Requested</p>
            <p className="text-sm font-medium">{data.managerRequested ? 'Yes 🙋' : 'No'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Duration</p>
            <p className="text-sm font-medium">
              {data.finishedAt
                ? formatDuration(
                    new Date(data.finishedAt).getTime() - new Date(data.startedAt).getTime(),
                  )
                : 'In progress'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Messages thread */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversation Thread</CardTitle>
        </CardHeader>
        <CardContent>
          {data.messages.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No messages in this conversation
            </p>
          ) : (
            <div className="space-y-4">
              {data.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="max-w-[75%] space-y-1">
                    {/* Show role label and time */}
                    <div
                      className={`flex items-center gap-2 text-xs text-muted-foreground ${
                        msg.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <span className="font-medium">
                        {msg.role === 'user' ? '👤 Contact' : '🤖 AI Agent'}
                      </span>
                      <span>{new Date(msg.createdAt).toLocaleTimeString()}</span>
                    </div>

                    {/* Message bubble */}
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-tr-sm'
                          : 'bg-muted rounded-tl-sm'
                      }`}
                    >
                      {msg.content}
                    </div>

                    {/* Intent badge for user messages */}
                    {msg.intent && (
                      <div
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700">
                          Intent: {msg.intent}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}
